import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button/Button";
import StyledText from "components/atoms/text/StyledText";

import BodyWithBreadcrumb from "components/molecules/BodyWithBreadcrumb";
import Breadcrumb from "components/molecules/Breadcrumb";
import AbstractModal from "components/page/modal/AbstractModal";
import PaymentBillingHistoryModal from "components/page/modal/PaymentBillingHistoryModal";
import PaymentExtendSubscribeModal from "components/page/modal/PaymentExtendSubscribeModal";
import PaymentSubscribeModal from "components/page/modal/PaymentSubscribeModal";
import LoginUserLayout from "components/templates/LoginUserLayout";

import {
    usePaymentBillingHistory,
    usePaymentCoupon,
    usePaymentDiscountPolicy,
    usePaymentPlanSubscriptionHistory,
    usePaymentPlanSubscriptionHistoryUpdate,
} from "hooks/queries/usePayment";
import { useSpace } from "hooks/queries/useUser";
import { useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { dateKr } from "utils/dateUtil";

import moment from "moment";

const Container = styled.div`
    width: 100%;
`;
const PageTitle = styled(StyledText).attrs({
    fontSize: "large",
    fontWeight: "bold",
})``;

const TabSection = styled.div`
    display: flex;
    align-items: center;
`;

const TabButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 32px;
    background-color: var(--color-White);
    border: Transparent;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledText)")};
    }
`;

const TabButtonText = styled(StyledText)`
    color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledText)")};
`;

const TabViewSection = styled.div`
    margin: 32px 0 0 0;
    width: 100%;
    height: 100%;
    overflow: auto;
`;

const TabViewContainer = styled.form`
    ${(props) => (props.active ? "" : "display: none;")}

    position: relative;
    width: 100%;
`;

const SubscriptionSummaryCard = styled.div`
    width: 100%;
    padding: 12px 16px;
    border-radius: 4px;
    border: 1px solid var(--color-Outline);
    background-color: var(--color-White);

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const SubscriptionLeft = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const SubscriptionRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
    white-space: nowrap;
`;

const SubscriptionPlanName = styled(StyledText)`
    font-size: 1.4rem;
    font-weight: 500;
`;

const SubscriptionStatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;

    border-radius: 999px;
    border: 1px solid var(--color-Grey3);
    background-color: var(--color-Base1);

    font-size: 1.2rem;
    color: var(--color-SubBlack);
`;

const SubscriptionMetaText = styled(StyledText)`
    font-size: 1.2rem;
    font-weight: 400;
`;

const SubscriptionLink = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0 0 0 8px;
    font-size: 1.3rem;
    color: var(--color-SubBlack);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }
`;

const Section = styled.div`
    &:not(:first-of-type) {
        margin-top: 32px;
    }
`;

const SectionTitle = styled(StyledText).attrs({
    withBarType: true,
    fontSize: "regular",
    fontWeight: "bold",
    style: { display: "flex", alignItems: "center", gap: "8px" },
})``;

const SectionContents = styled.div`
    padding: ${(props) => props.padding || "16px 0 0 0"};
`;

const TableWrap = styled.div`
    margin: 8px 0 0 0;
    width: 100%;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    overflow: hidden;
`;

const Table = styled.table`
    width: 100%;
    padding: 0 0 160px 0;
`;

const Tr = styled.tr`
    border-bottom: solid 1px var(--color-Outline);
    overflow: hidden;

    &:last-child {
        border-bottom: transparent;
    }
`;

const Th = styled.th`
    padding: 12px 16px;
    width: ${(props) => props.width};
    background-color: var(--color-Base1);
    text-align: ${(props) => props.textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const Td = styled.td`
    padding: ${(props) => props.padding || "12px 16px"};
    background-color: var(--color-White);
    text-align: ${(props) => props.textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const ThText = styled(StyledText).attrs({
    fontSize: "regular",
    color: "var(--color-Black)",
})``;

const TdText = styled(StyledText).attrs({
    fontSize: "regular",
    color: "var(--color-SubBlack)",
})``;

const Amount = styled.span`
    margin: 0 4px 0 0;
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

function SpaceManagementPage() {
    const { space_uuid } = useParams();

    const [tabList, setTabList] = useState([]);
    const [tabActive, setTabActive] = useState("");

    const [user, setUser] = useState({});
    const userQuery = useUser();

    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    const spaceQuery = useSpace();

    const spaces = spaceQuery.data ?? [];

    const activeSpace = useMemo(() => spaces.find((space) => space.uuid === space_uuid) ?? null, [spaces, space_uuid]);

    const isPrivateSpace = !!activeSpace && activeSpace.group === null;

    useEffect(() => {
        if (isPrivateSpace) {
            setTabList(["payment"]);
            setTabActive("payment");
        } else {
            setTabList(["basicInfo", "payment"]);
            setTabActive("basicInfo");
        }
    }, [isPrivateSpace]);

    // 결제 이력
    const [paymentHistory, setPaymentHistory] = useState([]);
    const paymentHistoryQuery = usePaymentBillingHistory({ space_uuid: space_uuid });

    useEffect(() => {
        if (paymentHistoryQuery.data) {
            setPaymentHistory(paymentHistoryQuery.data);
        }
    }, [paymentHistoryQuery.data]);

    // 할인 정책
    const discountPolicyQuery = usePaymentDiscountPolicy();

    const showDiscountRate = (discountPolicy) => {
        let totalRate = 0;
        if (discountPolicy && discountPolicyQuery.data) {
            discountPolicy.forEach((policyId) => {
                const discountRate = discountPolicyQuery.data.find((policy) => policy.id === policyId);
                if (discountRate) {
                    totalRate += Number(discountRate.discount_rate);
                }
            });
        }
        return totalRate > 0 ? `(${totalRate}% 할인)` : "";
    };

    // 결제 이력 관련
    const [subscribeHistory, setSubscribeHistory] = useState({});
    const subscribeHistoryQuery = usePaymentPlanSubscriptionHistory();
    const subscribeHistoryUpdateMutation = usePaymentPlanSubscriptionHistoryUpdate();

    useEffect(() => {
        if (subscribeHistoryQuery.data) {
            const activeHistory = subscribeHistoryQuery.data.filter((subscribe) => subscribe.status === "ACTIVE");

            if (isPrivateSpace) {
                setSubscribeHistory(activeHistory.find((history) => history.plan === Constants.PAID_PLAN) || {});
            } else {
                console.log(activeHistory);
                console.log(activeSpace);
                const filteredHistory = activeHistory.find((history) => history?.group?.id === activeSpace?.group);
                console.log("filteredHistory:", filteredHistory);
                setSubscribeHistory(filteredHistory || {});
            }
        }
    }, [subscribeHistoryQuery.data, activeSpace]);

    // 쿠폰 관련
    const [couponList, setCouponList] = useState([]);
    const couponQuery = usePaymentCoupon();
    useEffect(() => {
        if (couponQuery.data) {
            setCouponList(couponQuery.data.filter((coupon) => !coupon.used_at));
        }
    }, [couponQuery.data]);

    const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(null);

    const isGroupMember = useMemo(() => {
        if (isPrivateSpace) {
            return false;
        }
        if (user && Object.keys(subscribeHistory).length > 0) {
            return user.email !== subscribeHistory?.group.admin_email;
        }
    }, [subscribeHistory, user, isPrivateSpace]);

    const [showExtendSubscribeModal, setShowExtendSubscribeModal] = useState(false);
    const [showPaidPlanSubscribeModal, setShowPaidPlanSubscribeModal] = useState(false);
    return (
        <LoginUserLayout>
            <HtmlHead title={"관리"} />
            <Breadcrumb>
                <PageTitle>관리</PageTitle>
            </Breadcrumb>
            <BodyWithBreadcrumb>
                <Container>
                    <TabSection>
                        {tabList.map((tab) => (
                            <TabButton
                                key={tab}
                                onClick={() => {
                                    if (tab === "payment" && isGroupMember) {
                                        Alert("info", "알림", "결제 정보는 그룹 관리자만 확인할 수 있습니다.");
                                        return;
                                    }
                                    setTabActive(tab);
                                }}
                                active={tabActive === tab}
                            >
                                <TabButtonText active={tabActive === tab}>
                                    {tab === "payment" ? "결제정보" : tab === "basicInfo" ? "기본정보" : ""}
                                </TabButtonText>
                            </TabButton>
                        ))}
                    </TabSection>
                    <TabViewSection>
                        {/* 기본정보 */}
                        <TabViewContainer active={tabActive === "basicInfo"}></TabViewContainer>

                        {/* 결제정보 */}
                        <TabViewContainer active={tabActive === "payment"}>
                            <Section>
                                <SectionContents padding={"0"}>
                                    <SubscriptionSummaryCard>
                                        <SubscriptionLeft>
                                            <SubscriptionPlanName>
                                                {isPrivateSpace
                                                    ? Object.keys(subscribeHistory).length === 0
                                                        ? "Basic"
                                                        : "Pro"
                                                    : "Group"}{" "}
                                                요금제 구독 중
                                            </SubscriptionPlanName>
                                            {Object.keys(subscribeHistory).length === 0 ? (
                                                <Button
                                                    onlyText
                                                    width={"88px"}
                                                    height={"32px"}
                                                    size={"small"}
                                                    buttonText={"업그레이드"}
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    onClick={() => {
                                                        setShowPaidPlanSubscribeModal(true);
                                                    }}
                                                />
                                            ) : subscribeHistory.is_scheduled === true ? (
                                                <>
                                                    <SubscriptionStatusPill>자동 연장 중</SubscriptionStatusPill>

                                                    <SubscriptionMetaText>
                                                        다음 결제일:{" "}
                                                        {moment(subscribeHistory?.ended_at).format("YYYY년 M월 D일")}
                                                    </SubscriptionMetaText>

                                                    <SubscriptionLink
                                                        type="button"
                                                        onClick={() => {
                                                            Confirm(
                                                                "warn",
                                                                "알림",
                                                                "자동 연장을 해지하겠습니까? \n이용 기간이 만료되면 요금제가 구독 종료됩니다.",
                                                                "해지",
                                                                () => {
                                                                    subscribeHistoryUpdateMutation.mutate(
                                                                        {
                                                                            id: subscribeHistory.id,
                                                                            is_scheduled: false,
                                                                        },
                                                                        {
                                                                            onSuccess: () => {
                                                                                Alert(
                                                                                    "info",
                                                                                    "구독",
                                                                                    "자동 연장이 해지되었습니다.",
                                                                                );
                                                                                subscribeHistoryQuery.refetch();
                                                                            },
                                                                        },
                                                                    );
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        해지
                                                    </SubscriptionLink>
                                                </>
                                            ) : (
                                                <>
                                                    <SubscriptionMetaText>
                                                        만료일:{" "}
                                                        {moment(subscribeHistory?.ended_at).format("YYYY년 M월 D일")}
                                                    </SubscriptionMetaText>

                                                    <SubscriptionLink
                                                        type="button"
                                                        onClick={() => {
                                                            setShowExtendSubscribeModal(subscribeHistory);
                                                        }}
                                                    >
                                                        연장하기
                                                    </SubscriptionLink>
                                                </>
                                            )}
                                        </SubscriptionLeft>

                                        <SubscriptionRight>
                                            {isPrivateSpace && (
                                                <>
                                                    <SubscriptionMetaText>
                                                        사용 가능한 쿠폰이{" "}
                                                        {couponList.length > 0 ? "있습니다." : "없습니다."}
                                                    </SubscriptionMetaText>
                                                    <Button
                                                        onlyText
                                                        width={"88px"}
                                                        height={"32px"}
                                                        size={"small"}
                                                        buttonText={"쿠폰 사용"}
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        onClick={() => {
                                                            if (subscribeHistory.id) {
                                                                setShowExtendSubscribeModal({
                                                                    ...subscribeHistory,
                                                                    is_only_coupon_use: true,
                                                                });
                                                            } else {
                                                                setShowPaidPlanSubscribeModal(true);
                                                            }
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </SubscriptionRight>
                                    </SubscriptionSummaryCard>
                                </SectionContents>
                            </Section>

                            <Section>
                                <SectionTitle>이용 내역</SectionTitle>
                                <SectionContents>
                                    <TableWrap>
                                        <Table>
                                            <tbody>
                                                <Tr>
                                                    <Th width="160px">
                                                        <ThText>일시</ThText>
                                                    </Th>
                                                    <Th textAlign="left">
                                                        <ThText>상세</ThText>
                                                    </Th>
                                                    <Th width="120px">
                                                        <ThText>영수증</ThText>
                                                    </Th>
                                                    <Th width="120px">
                                                        <ThText>금액</ThText>
                                                    </Th>
                                                </Tr>
                                                {paymentHistory.length === 0 ? (
                                                    <Tr style={{ height: "80px" }}>
                                                        <Td colSpan="4">
                                                            <TdText>이용 내역이 없습니다.</TdText>
                                                        </Td>
                                                    </Tr>
                                                ) : (
                                                    paymentHistory.map((history, index) => (
                                                        <Tr key={index}>
                                                            <Td>
                                                                <TdText>
                                                                    {moment(history.created_at).format(
                                                                        "YYYY.MM.DD HH:mm",
                                                                    )}
                                                                </TdText>
                                                            </Td>
                                                            <Td textAlign="left">
                                                                <TdText>
                                                                    {history.description}{" "}
                                                                    {showDiscountRate(history?.discount_policy)}
                                                                </TdText>

                                                                {history.started_at && history.ended_at && (
                                                                    <TdText style={{ marginTop: "4px" }}>
                                                                        이용기간 : {dateKr(moment(history.started_at))}{" "}
                                                                        ~ {dateKr(moment(history.ended_at))}
                                                                    </TdText>
                                                                )}
                                                            </Td>
                                                            <Td>
                                                                <DetailRow>
                                                                    {history.amount > 0 &&
                                                                        history.status === "COMPLETED" && (
                                                                            <Button
                                                                                onlyText
                                                                                width={"60px"}
                                                                                height={"24px"}
                                                                                fontColor={"var(--color-White)"}
                                                                                size={"xsmall"}
                                                                                buttonText={"영수증"}
                                                                                bgColor={"var(--color-Button2)"}
                                                                                hoverBgColor={
                                                                                    "var(--color-ButtonHover2)"
                                                                                }
                                                                                onClick={(e) => {
                                                                                    setShowPaymentHistoryModal(
                                                                                        history.id,
                                                                                    );
                                                                                }}
                                                                            />
                                                                        )}
                                                                </DetailRow>
                                                            </Td>
                                                            <Td textAlign="right" padding="0 24px 0 0">
                                                                <TdText>
                                                                    {history.amount !== null && (
                                                                        <>
                                                                            <Amount>{history.amount}</Amount>원
                                                                        </>
                                                                    )}
                                                                </TdText>
                                                            </Td>
                                                        </Tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </TableWrap>
                                </SectionContents>
                            </Section>
                        </TabViewContainer>
                    </TabViewSection>
                </Container>
            </BodyWithBreadcrumb>
            {showPaidPlanSubscribeModal && (
                <AbstractModal
                    modalTitle="요금제 결제"
                    width={500}
                    height={640}
                    exitModal={(e) => setShowPaidPlanSubscribeModal(!showPaidPlanSubscribeModal)}
                >
                    <PaymentSubscribeModal
                        // plan={}
                        user={user}
                        exitModal={(e) => setShowPaidPlanSubscribeModal(!showPaidPlanSubscribeModal)}
                    />
                </AbstractModal>
            )}
            {showPaymentHistoryModal && (
                <AbstractModal
                    modalTitle=""
                    width={500}
                    height={640}
                    exitModal={(e) => setShowPaymentHistoryModal(null)}
                >
                    <PaymentBillingHistoryModal id={showPaymentHistoryModal} user={user} />
                </AbstractModal>
            )}
            {showExtendSubscribeModal && (
                <AbstractModal
                    modalTitle="이용 기간 연장"
                    width={500}
                    height={640}
                    exitModal={(e) => setShowExtendSubscribeModal(false)}
                >
                    <PaymentExtendSubscribeModal
                        user={user}
                        isGroupPlan={showExtendSubscribeModal.plan === Constants.GROUP_PLAN}
                        groupInfo={showExtendSubscribeModal.group}
                        subscription={showExtendSubscribeModal}
                        exitModal={(e) => setShowExtendSubscribeModal(false)}
                        isOnlyCouponUse={showExtendSubscribeModal.is_only_coupon_use}
                    />
                </AbstractModal>
            )}

            <div
                style={{
                    display: "none",
                    overFlow: "scroll",
                    position: "fixed",
                    zIndex: 10000,
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    padding: "120px 0",
                    background: "rgba(0,0,0,.48)",
                    boxSizing: "border-box",
                }}
                id={"iamport-modal"}
            ></div>
        </LoginUserLayout>
    );
}

export default SpaceManagementPage;
